import ESpeakNg from 'espeak-ng'
import {Language, transliterationRepresentationIds} from '@shared/types'
import {armenianToIpa} from './armenian-ipa'
import {ipaToCyrillic} from './ipa-cyrillic'
import {ipaLatin} from './ipa-latin'

export type TransliterationRepresentationId = (typeof transliterationRepresentationIds)[number]

export interface LanguageToIpaPlugin {
  id: string
  language: Language
  standard: string
  convert: (text: string) => Promise<string>
}

export interface IpaToRepresentationPlugin {
  id: string
  representationId: Exclude<TransliterationRepresentationId, 'ipa'>
  labelKey: string
  convert: (ipa: string) => string
}

export interface TransliterationRepresentation {
  id: TransliterationRepresentationId
  labelKey: string
  convert: (text: string) => Promise<string>
}

export interface TransliterationResult {
  id: TransliterationRepresentationId
  labelKey: string
  text: string
}

const POLISH_IPA_OUTPUT = 'polish-ipa'

// Реестр A: из изучаемого языка в IPA. Один плагин на язык.
export const languageToIpaPlugins: Partial<Record<Language, LanguageToIpaPlugin>> = {
  [Language.Armenian]: {
    id: 'hy-ipa',
    language: Language.Armenian,
    standard: 'ipa-eastern-native-rules',
    convert: async (text) => armenianToIpa(text),
  },
  [Language.Polish]: {
    id: 'pl-ipa',
    language: Language.Polish,
    standard: 'espeak-ng-pl',
    convert: phonemizePolish,
  },
}

// Реестр B: из IPA в представление. Один плагин на представление.
export const ipaToRepresentationPlugins: Record<string, IpaToRepresentationPlugin> = {
  'ipa-cyrillic': {
    id: 'ipa-cyrillic',
    representationId: 'cyrillic',
    labelKey: 'transliteration.cyrillic',
    convert: ipaToCyrillic,
  },
  'ipa-latin': {
    id: 'ipa-latin',
    representationId: 'latin',
    labelKey: 'transliteration.latin',
    convert: ipaLatin,
  },
}

// Нормализация прямого вывода IPA пакета: без tie bar и придыхания, первая буква строчная.
function normalizeIpaText(text: string): string {
  return text
    .trim()
    .replace(/\u200D/g, '')
    .replace(/^(.)/, (_, character: string) => character.toLowerCase())
}

const polishIpaCache = new Map<string, Promise<string>>()

function phonemizePolish(text: string): Promise<string> {
  const cached = polishIpaCache.get(text)
  if (cached) return cached

  const ipaPromise = ESpeakNg({
    arguments: ['--phonout', POLISH_IPA_OUTPUT, '--sep=', '-q', '--ipa=3', '-v', 'pl', text],
  }).then((espeak) => normalizeIpaText(espeak.FS.readFile(POLISH_IPA_OUTPUT, {encoding: 'utf8'})))
  polishIpaCache.set(text, ipaPromise)
  return ipaPromise
}

// Построение представлений для языка: общие для всех языков, доступны,
// если в реестре A есть плагин этого языка.
export function getRepresentations(language: Language): TransliterationRepresentation[] {
  const base = languageToIpaPlugins[language]
  if (!base) return []
  const rawIpa = (text: string): Promise<string> => base.convert(text)
  return [
    {
      id: 'ipa',
      labelKey: 'transliteration.ipa',
      convert: async (text) => normalizeIpaText(await rawIpa(text)),
    },
    ...Object.values(ipaToRepresentationPlugins).map((plugin) => ({
      id: plugin.representationId,
      labelKey: plugin.labelKey,
      convert: async (text: string) => plugin.convert(await rawIpa(text)),
    })),
  ]
}

export async function transliterateForeign(
  text: string,
  targetLanguage: Language,
  selectedIds: string[]
): Promise<TransliterationResult[]> {
  if (!text || selectedIds.length === 0) return []
  const representations = getRepresentations(targetLanguage)
  const results: TransliterationResult[] = []
  for (const representation of representations) {
    if (!selectedIds.includes(representation.id)) continue
    const converted = await representation.convert(text)
    if (converted) results.push({id: representation.id, labelKey: representation.labelKey, text: converted})
  }
  return results
}
